import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { fetchCurrentUser } from '@/lib/auth';
import {
  Check,
  X as XIcon,
  ChevronDown,
  UserPlus,
  Paperclip,
  Pencil,
  Trash2,
  Download,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
} from 'lucide-react';
import {
  Button,
  Chip,
  Modal,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import {
  useDetail,
  useDossieTexto,
  useMoverEstagio,
  useAtributos,
  useUsers,
  useEditarComentario,
  useExcluirComentario,
  useUploadAnexoComentario,
  useExcluirAnexoComentario,
  useComentar,
} from '../api';
import { FaseAnteriorCard } from './FaseAnteriorCard';
import { ImovelCadastroTab } from './ImovelCadastroTab';
import { MoverFasePanel } from './MoverFasePanel';
import { FaseTriagem } from './fases/FaseTriagem';
import { FaseDossie } from './fases/FaseDossie';
import { FaseProposta } from './fases/FaseProposta';
import { FaseAbordagem } from './fases/FaseAbordagem';
import { FaseQualificacao } from './fases/FaseQualificacao';
import { statusTone } from '../data';
import { categoriaDaVerificacao, CATEGORIA_CLASS } from '../categorias';
import { TEMP_CHIP } from '../temperatura';

// verificacao -> id da camada no mapa-desenho (routes/imoveis.js). O mapa abre
// com a evidencia do sinal acesa: quem ve a sobreposicao decide melhor que quem
// le "UC sobrepoe X". Sem entrada = mapa abre sem camada tematica.
const CAMADA_DO_SINAL: Record<string, string> = {
  prodes: 'prodes',
  deter: 'deter',
  mapbiomas: 'mapbiomas',
  cnfp: 'cnfp',
  uc: 'uc',
  ti: 'ti',
  quilombola: 'quilombola',
  sigef: 'sigef',
  assentamento: 'assent',
  ibama_embargo_geo: 'ibama',
  ibama_autos: 'ibama-autos',
  icmbio_autos: 'icmbio-autos',
  icmbio_embargo: 'icmbio-emb',
  embargo_estadual: 'sedam',
};

// Rotulo do valor cru. Nao da pra "capitalizar": siglas viram maiusculas e o
// acento nao existe no dado (sobreposicao -> Sobreposicao ficaria errado).
const VERIFICACAO_LABEL: Record<string, string> = {
  cnfp: 'Floresta Pública Tipo B',
  sigef: 'SIGEF / SNCI',
  prodes: 'PRODES',
  sicar: 'CAR',
  deter: 'DETER',
  mapbiomas: 'MapBiomas Alerta',
  embargo_estadual: 'Embargo estadual (SEDAM)',
  ccir_externo: 'CCIR externo',
  ibama_embargo_geo: 'Embargo IBAMA',
  ibama_autos: 'Auto de infração IBAMA',
  icmbio_autos: 'Auto de infração ICMBio',
  icmbio_embargo: 'Embargo ICMBio',
  sobreposicao: 'Sobreposição',
  uc: 'Unidade de Conservação',
  ti: 'Terra Indígena',
};
const rotuloVerificacao = (v: string | null | undefined) =>
  !v ? '—' : (VERIFICACAO_LABEL[v] ?? v);

// Etiqueta de triagem = esteira, com o ponto da cor da frente — igual ao card
// do kanban, pra o modal continuar o card em vez de recomecar.
const FRENTE_DOT: Record<string, string> = {
  ambiental: 'bg-[var(--frente-ambiental)]',
  fundiario: 'bg-[var(--frente-fundiario)]',
  credito: 'bg-[var(--frente-credito)]',
  juridico: 'bg-[var(--frente-juridico)]',
};


const iniciaisDe = (nome: string) =>
  nome.split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase() ?? '').join('');
const FRENTES_ESTEIRA: Array<{ id: 'ambiental' | 'fundiario' | 'credito' | 'juridico'; label: string }> = [
  { id: 'ambiental', label: 'Triagem Ambiental' },
  { id: 'fundiario', label: 'Triagem Fundiária' },
  { id: 'credito', label: 'Triagem de Crédito' },
  { id: 'juridico', label: 'Triagem Jurídica' },
];
const TRIAGEM_LABEL_MODAL: Record<string, string> = {
  ambiental: 'Triagem Ambiental',
  fundiario: 'Triagem Fundiária',
  credito: 'Triagem de Crédito',
  juridico: 'Triagem Jurídica',
};
import {
  TEMPERATURA_LABEL,
  type Card as CardData,
  type Estagio,
} from '../schemas';

interface Props {
  card: CardData | null;
  onClose: () => void;
}

// Select reusável para campos do perfil comportamental — sempre mostra todas as
// opções (datalist nativo filtra pelo valor atual e escondia as outras). Mantém
// uma opção dinâmica de compat para valores legados (enums lowercase ou texto
// livre antigo) que não casem com nenhum label do catálogo.

function formatDate(s: string | null | undefined) {
  if (!s) return null;
  try {
    return new Date(s).toLocaleString('pt-BR');
  } catch {
    return s;
  }
}



// Resumo enxuto de dados_raw — só campos planos legíveis (string/number/boolean).
// Evita despejar JSON cru no analista.
function dadosRawResumo(raw: unknown): Array<{ k: string; v: string }> {
  if (!raw || typeof raw !== 'object') return [];
  const out: Array<{ k: string; v: string }> = [];
  for (const [k, v] of Object.entries(raw)) {
    if (v === null || v === undefined) continue;
    const t = typeof v;
    if (t === 'string' && (v as string).length < 200) out.push({ k, v: v as string });
    else if (t === 'number' || t === 'boolean') out.push({ k, v: String(v) });
    if (out.length >= 12) break;
  }
  return out;
}

// Icone do anexo por tipo de arquivo (Figma 5199:454). Fallback FileText/cinza.
function anexoTipo(mime: string, nome: string): { Icon: typeof FileText; bg: string; fg: string } {
  const ext = (nome.split('.').pop() ?? '').toLowerCase();
  const is = (...vs: string[]) => vs.includes(ext) || vs.some((v) => mime.includes(v));
  if (is('pdf')) return { Icon: FileText, bg: 'rgba(220,38,38,0.10)', fg: '#dc2626' };
  if (is('xlsx', 'xls', 'csv', 'spreadsheet')) return { Icon: FileSpreadsheet, bg: 'rgba(33,115,70,0.12)', fg: '#217346' };
  if (is('doc', 'docx', 'word')) return { Icon: FileText, bg: 'rgba(43,87,154,0.12)', fg: '#2b579a' };
  if (is('jpg', 'jpeg', 'png', 'webp', 'gif', 'image')) return { Icon: ImageIcon, bg: 'rgba(124,58,237,0.12)', fg: '#7c3aed' };
  return { Icon: FileText, bg: 'var(--surface2)', fg: 'var(--text-muted)' };
}

function fmtTamanho(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',') + ' MB';
}

export function CrmDetailModal({ card, onClose }: Props) {
  const id = card?.id ?? null;
  const { data } = useDetail(id);
  const dossieTexto = useDossieTexto(id);
  const comentar = useComentar(id ?? '');
  const moverEstagio = useMoverEstagio();
  const [comentarioTxt, setComentarioTxt] = useState('');
  const [tab, setTab] = useState('atendimento');
  const [menuTriagem, setMenuTriagem] = useState(false);
  const [menuResp, setMenuResp] = useState(false);
  const atributos = useAtributos();
  const usersQ = useUsers();
  const editarComent = useEditarComentario(id ?? '');
  const excluirComent = useExcluirComentario(id ?? '');
  const uploadAnexo = useUploadAnexoComentario(id ?? '');
  const excluirAnexo = useExcluirAnexoComentario(id ?? '');
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 5 * 60_000,
  });
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editandoTexto, setEditandoTexto] = useState('');
  const novoAnexoRef = useRef<HTMLInputElement>(null);
  const editAnexoRef = useRef<HTMLInputElement>(null);
  const [confirmarExcluir, setConfirmarExcluir] = useState<number | null>(null);







  // Modal fica montado entre cards — reseta estados por-card pra não vazar
  // texto/escolha de um card pro outro (bug: comentário do card A sendo salvo no B).
  useEffect(() => {
    setComentarioTxt('');
  }, [card?.id]);

  if (!card) return null;

  const opp = data?.oportunidade ?? {};
  const estagio = (opp.estagio as Estagio | undefined) ?? card.estagio;
  const origem = data?.origem;

  const rawCampos = dadosRawResumo(origem?.dados_raw);

  // Mapa unificado (mapa-desenho) com a camada do sinal acesa. Iframe reusa a
  // pagina que ja existe — sem 2ª implementacao de Leaflet/PostGIS pra manter.
  const camadaAcesa = card.verificacao ? CAMADA_DO_SINAL[card.verificacao] : undefined;
  const mapaSrc = card.imovel_id
    ? `/api/v2/imoveis/${card.imovel_id}/mapa-desenho?embed=1${camadaAcesa ? `&camadas=${camadaAcesa}` : ''}`
    : '';

  function onComentar() {
    if (!comentarioTxt.trim()) return;
    comentar.mutate(comentarioTxt.trim(), {
      onSuccess: () => setComentarioTxt(''),
    });
  }

  // Cria o comentario e ja anexa o arquivo escolhido no botao de clipe.
  function onComentarComAnexo(file?: File) {
    if (!comentarioTxt.trim() && !file) return;
    comentar.mutate(comentarioTxt.trim() || '(anexo)', {
      onSuccess: (resp) => {
        setComentarioTxt('');
        const eid = Number((resp as { evento_id?: number })?.evento_id);
        if (file && eid) uploadAnexo.mutate({ eventoId: eid, file });
      },
    });
  }

  function podeEditar(ev: { tipo: string; ator_id?: number | null | undefined }) {
    if (!me || ev.tipo !== 'comentario') return false;
    if (me.role === 'admin') return true;
    return ev.ator_id != null && Number(ev.ator_id) === Number(me.id);
  }
  function iniciarEdicao(ev: { id: number; payload?: unknown }) {
    const pl = (ev.payload as Record<string, unknown> | null) ?? {};
    setEditandoId(ev.id);
    setEditandoTexto(typeof pl.texto === 'string' ? pl.texto : '');
  }
  function salvarEdicao() {
    if (editandoId == null || !editandoTexto.trim()) return;
    editarComent.mutate(
      { eventoId: editandoId, texto: editandoTexto.trim() },
      { onSuccess: () => { setEditandoId(null); setEditandoTexto(''); } },
    );
  }
  function excluirComentario(eventoId: number) {
    setConfirmarExcluir(eventoId);
  }
  function confirmarExclusao() {
    if (confirmarExcluir == null) return;
    excluirComent.mutate(confirmarExcluir, {
      onSuccess: () => setConfirmarExcluir(null),
    });
  }
  // Remove um anexo do comentario em edicao (X no chip).
  function removerAnexo(eventoId: number, filename: string) {
    excluirAnexo.mutate({ eventoId, filename });
  }
  // Anexa um novo documento a um comentario ja publicado (botao Anexar na edicao).
  function anexarNoEvento(eventoId: number, file?: File) {
    if (file) uploadAnexo.mutate({ eventoId, file });
  }


  return (
    <>
      <Modal
        open={!!card}
        onOpenChange={(o) => !o && onClose()}
        title={card.cliente_nome ?? card.cliente_cpf}
        description={[card.cliente_cpf, card.imovel_denominacao].filter(Boolean).join('  ·  ')}
        size="3xl"
        footer={
          id ? (
            <div className="flex w-full items-center justify-between gap-3">
              <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-text-muted">
                Mover para
              </span>
              <MoverFasePanel
                estagio={estagio}
                disabled={moverEstagio.isPending}
                onMove={(proximo) => moverEstagio.mutate({ id, estagio: proximo })}
                variant="inline"
              />
            </div>
          ) : undefined
        }
      >
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="atendimento">Atendimento</TabsTrigger>
            <TabsTrigger value="imovel">Cadastro do imóvel</TabsTrigger>
          </TabsList>

          <TabsContent value="atendimento">
        {/* MOVER_RODAPE: coluna unica; o mover-fase vai pro rodape no fim */}
        <div className="min-w-0">
        <Stack gap="md">
          {/* Status atual */}
          <div className="flex flex-wrap items-center gap-2">
            {/* status ANTES da triagem: "esta irregular -> vai pra esteira X" e a
                ordem da decisao. "Sinal detectado" saiu: a coluna do kanban ja
                diz a fase e era ruido no lugar mais nobre da tela. */}
            {origem?.status && (
              <Chip tone={statusTone(origem.status)}>
                {origem.status.charAt(0).toUpperCase() + origem.status.slice(1)}
              </Chip>
            )}
            {/* (7) etiqueta de triagem editavel: clica -> escolhe a esteira. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => { setMenuTriagem((v) => !v); setMenuResp(false); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-surface2 px-2 py-0.5 text-[0.72rem] font-medium text-text-2 hover:text-text transition-colors"
              >
                <span className={cn('inline-block h-1.5 w-1.5 rounded-full', FRENTE_DOT[card.frente])} />
                {TRIAGEM_LABEL_MODAL[card.frente]}
                <ChevronDown size={12} strokeWidth={2} className="text-text-muted" />
              </button>
              {menuTriagem && id && (
                <div className="absolute left-0 top-[calc(100%+4px)] z-50 w-52 rounded-xl border border-border bg-elevated p-1.5 shadow-[0_10px_28px_rgba(0,0,0,0.18)]">
                  {FRENTES_ESTEIRA.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => {
                        setMenuTriagem(false);
                        if (f.id !== card.frente) atributos.mutate({ id, frente: f.id });
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[0.8rem] transition-colors',
                        f.id === card.frente ? 'bg-[var(--pipe-active)] font-medium text-text' : 'text-text-2 hover:bg-surface2',
                      )}
                    >
                      <span className={cn('h-1.5 w-1.5 rounded-full', FRENTE_DOT[f.id])} />
                      <span className="flex-1">{f.label}</span>
                      {f.id === card.frente && <Check size={13} strokeWidth={2} className="text-text-muted" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {card.verificacao && (() => {
              // etiqueta de verificacao herda a cor da categoria (padrao Figma
              // 5182:447). Sem categoria mapeada (sobreposicao, triagem manual)
              // cai no chip neutro.
              const catV = categoriaDaVerificacao(card.verificacao);
              return catV ? (
                <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[0.72rem] font-medium', CATEGORIA_CLASS[catV])}>
                  {rotuloVerificacao(card.verificacao)}
                </span>
              ) : (
                <Chip tone="neutral">{rotuloVerificacao(card.verificacao)}</Chip>
              );
            })()}
            {card.fonte === 'reabertura' && <Chip tone="info">reabertura</Chip>}

            {/* (2) responsavel: chip com nome, ou "Atribuir responsavel". */}
            {id && (
              <div className="relative ml-auto">
                <button
                  type="button"
                  onClick={() => { setMenuResp((v) => !v); setMenuTriagem(false); }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-surface2 px-2 py-0.5 text-[0.72rem] font-medium text-text-2 hover:text-text transition-colors"
                >
                  {card.comercial_nome ? (
                    <>
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-surface3 text-[0.5rem] font-semibold text-text-2">
                        {iniciaisDe(card.comercial_nome)}
                      </span>
                      {card.comercial_nome.split(' ')[0]}
                    </>
                  ) : (
                    <>
                      <UserPlus size={12} strokeWidth={1.9} className="text-text-muted" />
                      Atribuir responsável
                    </>
                  )}
                  <ChevronDown size={12} strokeWidth={2} className="text-text-muted" />
                </button>
                {menuResp && id && (
                  <div className="absolute right-0 top-[calc(100%+4px)] z-50 max-h-64 w-60 overflow-y-auto rounded-xl border border-border bg-elevated p-1.5 shadow-[0_10px_28px_rgba(0,0,0,0.18)]">
                    {card.comercial_id != null && (
                      <button
                        type="button"
                        onClick={() => { setMenuResp(false); atributos.mutate({ id, comercial_id: null }); }}
                        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[0.8rem] text-text-2 hover:bg-surface2"
                      >
                        <XIcon size={13} strokeWidth={2} className="text-text-muted" />
                        Remover responsável
                      </button>
                    )}
                    {usersQ.isLoading && (
                      <div className="px-2.5 py-2 text-[0.78rem] text-text-muted">Carregando…</div>
                    )}
                    {(usersQ.data ?? [])
                      .filter((u) => u.name)
                      .map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setMenuResp(false);
                            if (u.id !== card.comercial_id) atributos.mutate({ id, comercial_id: u.id });
                          }}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[0.8rem] transition-colors',
                            u.id === card.comercial_id ? 'bg-surface2 font-medium text-text' : 'text-text-2 hover:bg-surface2',
                          )}
                        >
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface3 text-[0.55rem] font-semibold text-text-2">
                            {iniciaisDe(u.name ?? '')}
                          </span>
                          <span className="flex-1 truncate">{u.name}</span>
                          {u.id === card.comercial_id && <Check size={13} strokeWidth={2} className="text-text-muted" />}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}
            {card.temperatura && (
              <span
                className={
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[0.72rem] font-semibold ' +
                  TEMP_CHIP[card.temperatura]
                }
              >
                {TEMPERATURA_LABEL[card.temperatura]}
              </span>
            )}
          </div>

          {/* Mapa: iframe embed (?embed=1) — o unico mapa da tela. */}
          {card.imovel_id && (
            <section className="overflow-hidden rounded-xl bg-surface2" style={{ height: 338 }}>
              <iframe
                src={mapaSrc}
                title="Mapa do imóvel"
                className="h-full w-full border-0"
                loading="lazy"
                allow="fullscreen"
              />
            </section>
          )}

          {/* (6) Servico sugerido: ordem do Figma e mapa -> servico -> origem. */}
          {card.servico && (
            <div className="flex flex-col gap-1">
              <span className="text-[0.7rem] font-medium uppercase tracking-wide text-text-muted">
                Serviço sugerido
              </span>
              <span className="text-[0.95rem] font-medium text-text">{card.servico}</span>
            </div>
          )}

          {/* Fundo tonal sem borda (Figma), nao border+rounded. Sem "Ampliar":
              o mapa antigo (sub-modal mapTarget) foi removido — o tela cheia
              agora e um controle DENTRO do mapa. */}
          <section className="rounded-xl bg-surface2 p-4">
            <h3 className="mb-3 text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
              Origem do sinal
            </h3>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-[0.7rem] uppercase tracking-wide text-text-muted">Cliente</dt>
                <dd className="text-text">{card.cliente_nome ?? '—'}</dd>
                <dd className="text-text-2 text-[0.78rem]">{card.cliente_cpf}</dd>
              </div>
              {card.imovel_denominacao && (
                <div>
                  <dt className="text-[0.7rem] uppercase tracking-wide text-text-muted">Imóvel</dt>
                  <dd className="text-text">{card.imovel_denominacao}</dd>
                  {card.imovel_matricula && (
                    <dd className="text-text-2 text-[0.78rem]">
                      Matrícula {card.imovel_matricula}
                    </dd>
                  )}
                </div>
              )}
              <div>
                <dt className="text-[0.7rem] uppercase tracking-wide text-text-muted">Verificação</dt>
                <dd className="text-text">{rotuloVerificacao(card.verificacao)}</dd>
              </div>
              {origem?.verificado_em && (
                <div>
                  <dt className="text-[0.7rem] uppercase tracking-wide text-text-muted">Última varredura</dt>
                  <dd className="text-text">{formatDate(origem.verificado_em)}</dd>
                </div>
              )}
              {card.municipio && (
                <div>
                  <dt className="text-[0.7rem] uppercase tracking-wide text-text-muted">Município</dt>
                  <dd className="text-text">{card.municipio}</dd>
                </div>
              )}
            </dl>

            {(origem?.detalhe || card.detalhe) && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="text-[0.7rem] uppercase tracking-wide text-text-muted mb-1">
                  Detalhe técnico
                </div>
                <p className="text-sm text-text">{origem?.detalhe ?? card.detalhe}</p>
              </div>
            )}

            {rawCampos.length > 0 && (
              <details className="mt-3 pt-3 border-t border-border">
                <summary className="text-[0.78rem] text-text-2 cursor-pointer hover:text-text">
                  Dados brutos da verificação ({rawCampos.length})
                </summary>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-[0.78rem]">
                  {rawCampos.map((r) => (
                    <div key={r.k}>
                      <dt className="text-text-muted">{r.k}</dt>
                      <dd className="text-text font-mono break-all">{r.v}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            )}
          </section>

          {(estagio === 'sinal_detectado' || estagio === 'triagem') && id && (
            <FaseTriagem id={id} />
          )}

          {estagio === 'qualificacao' && id && (
            <FaseQualificacao
              id={id}
              cardId={card.id}
              detail={data}
              clienteCpf={card.cliente_cpf}
            />
          )}

          {estagio === 'dossie' && id && (
            <FaseDossie
              id={id}
              fallbackStatus={String(opp.dossie_status ?? card.dossie_status ?? 'pendente')}
            />
          )}

          {estagio === 'abordagem' && id && (
            <FaseAbordagem id={id} detail={data} cardId={card.id} />
          )}

          {estagio === 'proposta' && id && (
            <FaseProposta id={id} detail={data} clienteCpf={card.cliente_cpf} />
          )}

          {/* Comentário (Figma 5189/5199): campo tonal + anexar, comentários com
              anexo tipado + editar/excluir do próprio autor. */}
          <section>
            <h3 className="mb-2 text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
              Adicionar comentário
            </h3>
            <div className="flex items-center gap-2.5">
              <input
                type="text"
                value={comentarioTxt}
                onChange={(e) => setComentarioTxt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') onComentar(); }}
                placeholder="Registre o que foi apurado neste sinal…"
                className="h-10 min-w-0 flex-1 rounded-[10px] bg-surface2 px-3 text-sm text-text outline-none transition-shadow placeholder:text-text-muted focus:ring-2 focus:ring-[var(--accent-soft)]"
              />
              <input
                ref={novoAnexoRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onComentarComAnexo(f);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                title="Anexar documento"
                onClick={() => novoAnexoRef.current?.click()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-surface2 text-text-2 transition-colors hover:text-text"
              >
                <Paperclip size={16} strokeWidth={1.8} />
              </button>
              <Button
                variant="ghost"
                onClick={onComentar}
                disabled={comentar.isPending || !comentarioTxt.trim()}
              >
                Comentar
              </Button>
            </div>

            {(() => {
              const comentarios = (data?.eventos ?? []).filter((e) => e.tipo === 'comentario');
              if (comentarios.length === 0) return null;
              return (
                <ul className="mt-3 flex flex-col gap-2">
                  {comentarios.map((e) => {
                    const pl = (e.payload as Record<string, unknown> | null) ?? {};
                    const texto = typeof pl.texto === 'string' ? pl.texto : '';
                    const anexos = e.anexos ?? [];
                    const editando = editandoId === e.id;
                    const editavel = podeEditar(e);
                    return (
                      <li
                        key={e.id}
                        className={cn(
                          'rounded-[10px] px-3 py-2.5',
                          editando
                            ? 'bg-elevated ring-1 ring-accent'
                            : 'bg-surface2',
                        )}
                      >
                        <div className="mb-1 flex items-center gap-2 text-[0.7rem] text-text-muted">
                          <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-surface3 text-[0.55rem] font-semibold text-text-2">
                            {iniciaisDe(e.ator_nome ?? 'Sistema')}
                          </span>
                          <span className="font-medium text-text-2">{e.ator_nome ?? 'Sistema'}</span>
                          <span>·</span>
                          <span>{formatDate(e.created_at)}</span>
                          {e.editado_em && <span className="text-text-muted">· editado</span>}
                          <span className="flex-1" />
                          {editando ? (
                            <span className="rounded-full bg-[var(--accent-soft)] px-2 py-0.5 text-[0.62rem] font-medium text-accent">
                              Editando
                            </span>
                          ) : (
                            editavel && (
                              <span className="flex items-center gap-1">
                                <button
                                  type="button"
                                  title="Editar"
                                  onClick={() => iniciarEdicao(e)}
                                  className="flex h-6 w-6 items-center justify-center rounded-md text-text-muted hover:bg-surface3 hover:text-text-2"
                                >
                                  <Pencil size={13} strokeWidth={1.8} />
                                </button>
                                <button
                                  type="button"
                                  title="Excluir"
                                  onClick={() => excluirComentario(e.id)}
                                  className="flex h-6 w-6 items-center justify-center rounded-md text-text-muted hover:bg-surface3 hover:text-danger"
                                >
                                  <Trash2 size={13} strokeWidth={1.8} />
                                </button>
                              </span>
                            )
                          )}
                        </div>

                        {editando ? (
                          <div className="flex flex-col gap-2">
                            <textarea
                              value={editandoTexto}
                              onChange={(ev) => setEditandoTexto(ev.target.value)}
                              rows={2}
                              className="w-full resize-none rounded-[10px] bg-surface2 px-3 py-2 text-[0.82rem] text-text outline-none focus:ring-2 focus:ring-[var(--accent-soft)]"
                            />
                            {/* Anexos removiveis + Anexar novo (Figma 5202). */}
                            <div className="flex flex-wrap items-center gap-2">
                              {anexos.map((ax) => {
                                const { Icon, bg, fg } = anexoTipo(ax.mime, ax.nome);
                                return (
                                  <span
                                    key={ax.filename}
                                    className="inline-flex items-center gap-2 rounded-lg border border-border bg-elevated py-1.5 pl-2 pr-1.5"
                                  >
                                    <span
                                      className="flex h-[26px] w-[26px] items-center justify-center rounded-md"
                                      style={{ backgroundColor: bg, color: fg }}
                                    >
                                      <Icon size={14} strokeWidth={1.8} />
                                    </span>
                                    <span className="flex flex-col">
                                      <span className="text-[0.75rem] font-medium text-text">{ax.nome}</span>
                                      <span className="text-[0.65rem] text-text-muted">{fmtTamanho(ax.tamanho)}</span>
                                    </span>
                                    <button
                                      type="button"
                                      title="Remover anexo"
                                      onClick={() => removerAnexo(e.id, ax.filename)}
                                      disabled={excluirAnexo.isPending}
                                      className="flex h-5 w-5 items-center justify-center rounded-full bg-surface2 text-text-muted transition-colors hover:bg-surface3 hover:text-danger disabled:opacity-50"
                                    >
                                      <XIcon size={12} strokeWidth={2} />
                                    </button>
                                  </span>
                                );
                              })}
                              <input
                                ref={editAnexoRef}
                                type="file"
                                className="hidden"
                                onChange={(ev) => {
                                  const f = ev.target.files?.[0];
                                  if (f) anexarNoEvento(e.id, f);
                                  ev.target.value = '';
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => editAnexoRef.current?.click()}
                                disabled={uploadAnexo.isPending}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border2 px-2.5 py-1.5 text-[0.75rem] font-medium text-text-muted transition-colors hover:text-text-2 disabled:opacity-50"
                              >
                                <Paperclip size={13} strokeWidth={1.8} />
                                Anexar
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditandoId(null); setEditandoTexto(''); }}
                              >
                                Cancelar
                              </Button>
                              <span className="flex-1" />
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={salvarEdicao}
                                disabled={editarComent.isPending || !editandoTexto.trim()}
                              >
                                Salvar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap text-[0.82rem] leading-snug text-text">
                            {texto}
                          </p>
                        )}

                        {!editando && anexos.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {anexos.map((ax) => {
                              const { Icon, bg, fg } = anexoTipo(ax.mime, ax.nome);
                              return (
                                <a
                                  key={ax.filename}
                                  href={ax.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-2 rounded-lg border border-border bg-elevated px-2.5 py-1.5 hover:bg-surface2"
                                >
                                  <span
                                    className="flex h-[26px] w-[26px] items-center justify-center rounded-md"
                                    style={{ backgroundColor: bg, color: fg }}
                                  >
                                    <Icon size={14} strokeWidth={1.8} />
                                  </span>
                                  <span className="flex flex-col">
                                    <span className="text-[0.75rem] font-medium text-text">{ax.nome}</span>
                                    <span className="text-[0.65rem] text-text-muted">{fmtTamanho(ax.tamanho)}</span>
                                  </span>
                                  <Download size={14} strokeWidth={1.8} className="ml-1 text-text-muted" />
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </section>

          {/* Cards colapsáveis das fases anteriores — clicar abre os campos preenchidos */}
          <FaseAnteriorCard
            estagio={estagio}
            eventos={data?.eventos ?? []}
            oportunidade={opp}
            dossieTexto={dossieTexto.data?.dossie_texto ?? null}
          />

        </Stack>
        </div>
          </TabsContent>

          <TabsContent value="imovel">
            <ImovelCadastroTab
              imovel={data?.imovel}
              verificacao={card.verificacao}
            />
          </TabsContent>
        </Tabs>
      </Modal>

      {/* Confirmacao de exclusao de comentario (Figma 5201). Dialog proprio pra
          ter o circulo vermelho tonal + botao danger solido — o slot de icone da
          primitiva Modal e coral fixo. */}
      <Dialog.Root
        open={confirmarExcluir != null}
        onOpenChange={(o) => { if (!o) setConfirmarExcluir(null); }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[2100] bg-black/55 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-[2100] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-border bg-elevated shadow-[var(--shadow-over)] focus:outline-none">
            <div className="flex flex-col gap-3 px-6 pt-6 pb-5">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full text-danger"
                style={{ backgroundColor: 'color-mix(in srgb, var(--danger) 12%, transparent)' }}
              >
                <Trash2 size={20} strokeWidth={1.8} />
              </span>
              <Dialog.Title className="text-[1.03rem] font-semibold tracking-tight text-text">
                Excluir comentário?
              </Dialog.Title>
              <Dialog.Description className="text-[0.84rem] leading-relaxed text-text-2">
                O comentário e os documentos anexados a ele serão removidos. Esta ação não pode ser desfeita.
              </Dialog.Description>
            </div>
            <div className="flex justify-end gap-2 border-t border-border bg-surface2 px-6 py-3.5">
              <Button variant="ghost" onClick={() => setConfirmarExcluir(null)}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={confirmarExclusao}
                disabled={excluirComent.isPending}
                className="inline-flex items-center gap-1.5 rounded-full bg-danger px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                <Trash2 size={14} strokeWidth={1.8} />
                Excluir
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
